import React, {ChangeEvent, FC, FormEvent, useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router";
import Breadcrumb, {Crumb} from "../../../../components/common/Breadcrumbs";
import {useGetEnumsQuery} from "../../../../app/globalApi";
import AuditableInfoButton from "../../../../components/common/form/AuditableInfoButton";
import Form from "../../../../components/common/form/Form";
import InputField from "../../../../components/common/form/InputField";
import SelectField from "../../../../components/common/form/SelectField";
import {createOptionListForSelectTag} from "../../../../helpers/utils/Utils";
import ButtonTray from "../../../../components/common/form/ButtonTray";
import {hasPermission} from "../../../../helpers/utils/AccessControlUtils";
import {
    useCreateScraperModuleMutation,
    useDeleteScraperModuleByIdMutation,
    useGetScraperModuleByIdQuery,
    useGetScraperModulesListQuery,
    useUpdateScraperModuleMutation
} from "../scraperModuleApi";
import {handleRTKQuery} from "../../../../helpers/utils/RtkQueryUtils";
import {Routes as AppRoutes} from "../../../../routes";
import ConfirmationModal from "../../../../components/layout/modal/ConfirmationModal";
import IconButton from "../../../../components/common/form/IconButton";
import ModalButton from "../../../../components/common/form/ModalButton";
import {FaTrash} from "react-icons/fa6";
import {getEnumDisplayNameByConstant} from "../../../../helpers/utils/EnumUtils";

interface Props {
    crumbs: Crumb[];
    title: string;
    btnText: string;
    isEditable?: boolean;
}

const ScraperModuleFormField: FC<Props> = ({crumbs, title, btnText, isEditable}) => {
    const navigate = useNavigate();
    const {moduleId} = useParams();

    // State
    const [scrapperModuleData, setScrapperModuleData] = useState<any>({});

    // API Calls
    const {data: enums} = useGetEnumsQuery("");
    const {data: moduleData} = useGetScraperModuleByIdQuery(moduleId);
    const {data: scrapperDataList} = useGetScraperModulesListQuery("");

    const [createModuleApi, createModuleApiResponse] = useCreateScraperModuleMutation();
    const [updateModuleApi, updateModuleApiResponse] = useUpdateScraperModuleMutation();
    const [deleteApi, {isLoading}] = useDeleteScraperModuleByIdMutation();

    // Populate state when moduleData is fetched
    useEffect(() => {
        if (btnText === 'Update' && moduleData) {
            setScrapperModuleData({
                name: moduleData.name,
                url: moduleData.url,
                moduleKey: moduleData.moduleKey,
                proxyService: moduleData?.proxyService
                    ? {
                        value: moduleData.proxyService,
                        label: getEnumDisplayNameByConstant(
                            enums?.["ProxyService"],
                            moduleData.proxyService
                        ),
                    }
                    : null,
                preferredType: moduleData?.preferredType
                    ? {
                        value: moduleData.preferredType,
                        label: getEnumDisplayNameByConstant(
                            enums?.["ScraperJobType"],
                            moduleData.preferredType
                        ),
                    }
                    : null,
                supportingModules: moduleData?.supportingModules?.map((el: any) => ({
                    label: el?.name,
                    value: el?.id,
                }))

            })
        }
    }, [moduleData]);

    // Handle Form Submission
    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const updatedData = {
            name: scrapperModuleData?.name,
            url: scrapperModuleData.url,
            moduleKey: scrapperModuleData.moduleKey,
            supportingModules: scrapperModuleData?.supportingModules?.map(
                (module: any) => ({id: module.value})
            ) ?? [],

            proxyService: scrapperModuleData?.proxyService?.value,
            preferredType: scrapperModuleData?.preferredType?.value,
        };

        const createOrUpdateModule = async () => {
            if (btnText === "Create") {
                return await createModuleApi(updatedData).unwrap();
            } else {
                return await updateModuleApi({id: moduleId, requestBody: updatedData}).unwrap();
            }
        };

        await handleRTKQuery(createOrUpdateModule, (response: any) => {
             if (response?.success || response?.data?.success) {
                setScrapperModuleData({});
                navigate(AppRoutes.ScrapperModule.path);
            }
        })
    };

    // Handle Input Changes
    const handleOnChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | { name: string; value: any }) => {
        const {name, value} = "target" in e ? e.target : e;
        setScrapperModuleData((prev: any) => ({...prev, [name]: value}));
    };

    const handleDelete = async () => {
        if (moduleId) {
            await deleteApi(moduleId).unwrap();
        }
        navigate(AppRoutes.ScrapperModule.path)
    };

    return (
        <div className="px-4 py-2">
            <div className={"justify-between sm:block md:flex lg:flex"}>
                <Breadcrumb crumbs={crumbs}/>
                <div className={"justify-between sm:block md:flex lg:flex"}>
                    {moduleId && (
                        <>
                            <ModalButton
                                triggerButton={
                                    <IconButton
                                        icon={<FaTrash/>}
                                        title="Delete Note Logic"
                                        hasBackground={true}
                                        className="mr-2"
                                    />
                                }
                                modal={
                                    <ConfirmationModal
                                        show={true}
                                        setShow={() => {
                                        }}
                                        title="Delete Scrapper Module"
                                        bodyMessage="Are you sure you want to delete this Scraper Module?"
                                        onSubmit={() => handleDelete()}
                                        isLoading={isLoading}
                                    />
                                }
                            />
                            { hasPermission( ["AUDIT-V"]) &&
                                <AuditableInfoButton obj={moduleData}/>
                            }
                        </>
                    )}

                </div>
            </div>


            <div className="flex justify-center mt-3">
                <div className="bg-text-0 lg:w-4/6 sm:w-full p-5 rounded-md shadow-[0_0_10px_-2px_rgba(0,0,0,0.2)]">
                    <h3 className="text-xl font-semibold text-surface-0 pb-3 border-b border-surface-7">
                        {title}
                    </h3>

                    <Form onSubmit={handleSubmit} fields={
                        <>
                            <InputField
                                name="name"
                                label="Name"
                                onChange={handleOnChange}
                                value={scrapperModuleData?.name || ""}
                                required
                                placeholder="Enter Name..."
                            />
                            <InputField
                                name="url"
                                label="Url"
                                onChange={handleOnChange}
                                value={scrapperModuleData?.url || ""}
                                required
                                placeholder="Enter Url..."
                            />
                            <InputField
                                name="moduleKey"
                                label="Module Key"
                                onChange={handleOnChange}
                                value={scrapperModuleData?.moduleKey || ""}
                                required
                                placeholder="Enter Module Key..."
                            />

                            <SelectField
                                required
                                label="Preferred Type"
                                value={scrapperModuleData?.preferredType}
                                options={createOptionListForSelectTag(enums?.["ScraperJobType"], "value", "constant")}
                                onChange={(option) => handleOnChange({name: "preferredType", value: option})}
                            />

                            <SelectField
                                label="Supporting Modules"
                                value={scrapperModuleData?.supportingModules}
                                options={createOptionListForSelectTag(scrapperDataList, "name", "id")}
                                onChange={(option) => handleOnChange({name: "supportingModules", value: option})}
                                isMulti
                            />
                            <SelectField
                                label="Proxy Type"
                                value={scrapperModuleData?.proxyService}
                                options={createOptionListForSelectTag(enums?.["ProxyService"], "value", "constant")}
                                onChange={(option) => handleOnChange({name: "proxyService", value: option})}
                            />

                            <div className="col-span-12 md:grid md:grid-cols-12 gap-3">
                                <div className="col-span-3 sm:mt-2 lg:mt-0 mt-2 flex items-center">
                                </div>
                                <div className="relative col-span-9">
                                    <div className={"mt-4 flex"}>
                                        <ButtonTray buttons={[
                                            {
                                                buttonProps: {
                                                    btnText: "Cancel",
                                                    onClick: () => navigate(AppRoutes.ScrapperModule.path),
                                                    type: "cancel"
                                                },
                                                buttonType: "button"
                                            },
                                            {
                                                buttonProps: {
                                                    btnText: "Submit",
                                                    type: "submit",
                                                    isLoading: (createModuleApiResponse.isLoading || updateModuleApiResponse.isLoading),
                                                    isVisible: hasPermission(["CANNOTE-U"])
                                                },
                                                buttonType: "button"
                                            }
                                        ]} align="center" gap={2}/>
                                    </div>
                                </div>
                            </div>
                        </>
                    }/>
                </div>
            </div>
        </div>
    );
};

export default ScraperModuleFormField;
